import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import apiClient from '../services/api/apiClient'
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from '../services/auth/tokenStore'

const SecurityContext = createContext()

export const useAuth = () => useContext(SecurityContext)

export const SecurityContextProvider = ({ children }) => {
    const [token, setTokenState] = useState(getAccessToken())
    const [userData, setUserData] = useState(null)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [loading, setLoading] = useState(true)

    // Loads the current user from the access token's identity. Token attachment and silent
    // refresh are handled by apiClient, so there's no email decoding or manual header here.
    const fetchUserData = useCallback(async () => {
        if (!getAccessToken()) {
            setUserData(null)
            setIsAuthenticated(false)
            setLoading(false)
            return
        }
        try {
            const { data } = await apiClient.get('/users/me')
            setUserData(data)
            setIsAuthenticated(true)
        } catch (error) {
            // apiClient already attempted a refresh; a failure here means the session is dead.
            console.error('Failed to load current user', error)
            clearTokens()
            setTokenState(null)
            setUserData(null)
            setIsAuthenticated(false)
        } finally {
            setLoading(false)
        }
    }, [])

    // Called after Google sign-in with the backend's AuthResponse.
    const login = useCallback(async (authResponse) => {
        setTokens({ accessToken: authResponse.accessToken, refreshToken: authResponse.refreshToken })
        setTokenState(authResponse.accessToken)
        setLoading(true)
        await fetchUserData()
    }, [fetchUserData])

    const logout = useCallback(async () => {
        const refreshToken = getRefreshToken()
        if (refreshToken) {
            try {
                await apiClient.post('/auth/logout', { refreshToken })
            } catch (error) {
                // Best-effort revoke; clear local state regardless.
            }
        }
        clearTokens()
        setTokenState(null)
        setUserData(null)
        setIsAuthenticated(false)
    }, [])

    // Lets onboarding/profile flows refresh the cached user after a mutation.
    const refreshUser = useCallback(() => fetchUserData(), [fetchUserData])

    useEffect(() => {
        fetchUserData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <SecurityContext.Provider value={{
            userData,
            setUserData,
            login,
            logout,
            isAuthenticated,
            loading,
            token,
            refreshUser,
        }}>
            {children}
        </SecurityContext.Provider>
    )
}

export default SecurityContextProvider
